import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import PageShell from "@/components/opsiq/PageShell";

const CATEGORIES = [
  { name: "Leadership", items: ["Executive Dashboard", "Board Reporting", "Strategic KPI Scorecards"] },
  { name: "Finance & Growth", items: ["Finance", "Sales", "Marketing", "Procurement"] },
  { name: "Operations", items: ["Operations", "Inventory Management", "Supply Chain", "Manufacturing", "Asset Management", "Fleet Management"] },
  { name: "People & Customer", items: ["Human Resources", "Customer Service", "Branch Performance", "Retail Performance"] },
  { name: "Governance", items: ["Risk Management", "Compliance", "Audit Management", "Project Management"] },
  { name: "Industry-specific", items: ["Hospitality Management", "Healthcare Operations", "Education Management", "Agriculture & Farm Management"] },
];

export default function SolutionsPage() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <PageShell
      testid="solutions-page"
      eyebrow="Solutions · 25 modules"
      title={<>Every module. <span className="italic">One catalogue.</span></>}
      subtitle="Pick a module. Or build the full system. Every OpsIQ module is configured to your KPIs, hierarchy and reporting rhythm."
    >
      <div className="bg-[#F1ECE0] text-[#1A2E4A] border-t border-[#1A2E4A]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="border-t border-[#1A2E4A]/20">
            {CATEGORIES.map((cat, i) => {
              const isOpen = openIdx === i;
              return (
                <div key={cat.name} className="border-b border-[#1A2E4A]/20">
                  <button
                    data-testid={`sol-toggle-${i}`}
                    onClick={() => setOpenIdx(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between text-left py-5 md:py-6"
                  >
                    <div className="flex items-baseline gap-5 md:gap-8">
                      <span className="font-plex-mono text-[11px] uppercase tracking-[0.22em] text-[#1A2E4A]/50 w-10">0{i + 1}</span>
                      <span className="font-editorial text-2xl md:text-3xl lg:text-4xl leading-tight tracking-tight">{cat.name}</span>
                      <span className="hidden md:inline text-[11px] font-plex-mono uppercase tracking-[0.22em] text-[#1A2E4A]/50">{cat.items.length} modules</span>
                    </div>
                    <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                      <ChevronDown size={22} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-0 md:pl-[calc(2.5rem+2rem)] pb-6 md:pb-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                          {cat.items.map((m) => (
                            <div key={m} className="flex items-center gap-3 py-2.5 px-4 bg-white border border-[#1A2E4A]/12 hover:border-[#B8E024] transition-colors">
                              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#B8E024]" />
                              <span className="text-sm">{m}</span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
